import type { BuildingId, ResourceId, StackKind, StackMap } from '../types';
import { STACKS } from './resources';

/**
 * Storage by building level. 30. 9. 2026 (after the first pilot day: Storage never filled): about 70% of
 * the krug 5 values (400 / 800 / 1,400 / 2,200 / 3,200, then +800), so an active player fills it now and
 * then in the first two weeks and on at most about 10% of days (sim:progress 60 4, BALANCE_REPORT).
 */
// 5. 10. 2026 (owner, package 3): levels 1–3 +15% (300 / 600 / 1000 before), since the Crafter's Storage was over 80% on
// 1.5 more of the first 14 days with package 3 (the final pair; the condition is +1). 6. 10. 2026 (owner): levels 4 and 5
// +15% too (1500 / 2200 before): in the control every day over 80% in the first 14 was on level 4 or 5.
export const STORAGE_CAPACITY = [0, 345, 690, 1150, 1725, 2530, 2800, 3400, 4000, 4500, 5000];

/** A Storage level's line in the camp, from STORAGE_CAPACITY (30. 9. 2026: no longer typed by hand). */
const holds = (level: number) => `Holds ${STORAGE_CAPACITY[level].toLocaleString('en-US')}`;

export interface BuildingLevel {
  level: number;
  /** Cost to reach this level from the one before. */
  cost: Partial<Record<ResourceId, number>>;
  minutes: number;
  /** Minimum survivor level required to start the upgrade. */
  requiresLevel: number;
  effect: string;
}

export interface BuildingDef {
  id: BuildingId;
  name: string;
  description: string;
  phase: 1 | 2 | 3;
  maxLevel: number;
  /** Level 0 means not built yet. */
  startLevel: number;
  levels: BuildingLevel[];
}

export const BUILDINGS: Record<BuildingId, BuildingDef> = {
  workshop: {
    id: 'workshop', name: 'Workshop', phase: 1, maxLevel: 5, startLevel: 1,
    description: 'Crafting. The level sets the highest rarity you can craft.',
    levels: [
      { level: 1, cost: {}, minutes: 0, requiresLevel: 1, effect: 'Craft Common gear and basic consumables' },
      { level: 2, cost: { scrap: 40, metal: 20, wood: 10 }, minutes: 10, requiresLevel: 3, effect: 'Craft up to Uncommon' },
      { level: 3, cost: { scrap: 120, metal: 60, electronics: 25, wood: 20 }, minutes: 45, requiresLevel: 7, effect: 'Craft up to Rare · 2 jobs at once' },
      { level: 4, cost: { scrap: 300, metal: 150, electronics: 60, chemicals: 30 }, minutes: 180, requiresLevel: 11, effect: 'Craft up to Epic' },
      { level: 5, cost: { scrap: 600, metal: 300, electronics: 120, chemicals: 60, fuel: 30 }, minutes: 480, requiresLevel: 15, effect: 'Crafting is 20% faster · 3 jobs at once' },
    ],
  },
  storage: {
    id: 'storage', name: 'Storage', phase: 1, maxLevel: 10, startLevel: 1,
    description: 'Safe storage. Nothing in here can be lost in PvP.',
    levels: [
      // Krug 5 (F5): more room and less Wood, so a full Storage does not stall the run loop.
      { level: 1, cost: {}, minutes: 0, requiresLevel: 1, effect: holds(1) },
      { level: 2, cost: { scrap: 60, wood: 30, metal: 20 }, minutes: 15, requiresLevel: 2, effect: holds(2) },
      { level: 3, cost: { scrap: 150, wood: 40, metal: 50 }, minutes: 60, requiresLevel: 6, effect: holds(3) },
      { level: 4, cost: { scrap: 320, wood: 80, metal: 100, electronics: 20 }, minutes: 180, requiresLevel: 10, effect: holds(4) },
      { level: 5, cost: { scrap: 600, wood: 150, metal: 200, electronics: 60 }, minutes: 480, requiresLevel: 14, effect: holds(5) },
      // Krug 5 (K4.4b): levels 6–10, the late-game sink for Scrap, Metal and Wood (room: STORAGE_CAPACITY).
      // Costs from the K4.4b table, about 12% lower after sim:progress (the active player reaches 7–8 by day 60).
      { level: 6, cost: { scrap: 700, metal: 230, wood: 180, electronics: 70 }, minutes: 600, requiresLevel: 16, effect: holds(6) },
      { level: 7, cost: { scrap: 950, metal: 310, wood: 230, electronics: 90 }, minutes: 720, requiresLevel: 17, effect: holds(7) },
      { level: 8, cost: { scrap: 1300, metal: 410, wood: 300, electronics: 115 }, minutes: 840, requiresLevel: 18, effect: holds(8) },
      { level: 9, cost: { scrap: 1750, metal: 560, wood: 390, electronics: 150 }, minutes: 960, requiresLevel: 19, effect: holds(9) },
      { level: 10, cost: { scrap: 2350, metal: 750, wood: 500, electronics: 195 }, minutes: 1080, requiresLevel: 20, effect: holds(10) },
    ],
  },
  medical_tent: {
    id: 'medical_tent', name: 'Medical Tent', phase: 2, maxLevel: 5, startLevel: 0,
    description: 'Faster HP regeneration between expeditions.',
    levels: [
      { level: 1, cost: { scrap: 60, cloth: 30, medicine: 10 }, minutes: 20, requiresLevel: 5, effect: '+12% HP per hour at camp' },
      { level: 2, cost: { scrap: 120, cloth: 60, medicine: 25 }, minutes: 60, requiresLevel: 8, effect: '+24% HP per hour at camp' },
      { level: 3, cost: { scrap: 220, cloth: 90, medicine: 45, chemicals: 20 }, minutes: 150, requiresLevel: 11, effect: '+36% HP per hour at camp' },
      { level: 4, cost: { scrap: 380, cloth: 140, medicine: 80, chemicals: 40 }, minutes: 300, requiresLevel: 14, effect: '+48% HP per hour at camp' },
      { level: 5, cost: { scrap: 600, cloth: 200, medicine: 120, chemicals: 70 }, minutes: 480, requiresLevel: 17, effect: '+60% HP per hour at camp' },
    ],
  },
  barricade: {
    id: 'barricade', name: 'Barricade', phase: 3, maxLevel: 5, startLevel: 0,
    // Krug 5 (K4.17): both effects in plain words.
    // 1. 10. 2026: the scavenging crew comes with the first level (it was level 3); costs and levels as before.
    description: 'Walls around camp. It runs the scouts, and your tracks lose less when someone beats them.',
    levels: [
      { level: 1, cost: { scrap: 100, wood: 80, metal: 40 }, minutes: 30, requiresLevel: 8, effect: 'Unlocks the scouts (one team, 3 searches a loop), and your tracks lose 20% less when someone beats them' },
      { level: 2, cost: { scrap: 200, wood: 140, metal: 90 }, minutes: 90, requiresLevel: 10, effect: 'Your tracks lose 35% less when someone beats them' },
      { level: 3, cost: { scrap: 350, wood: 200, metal: 160, fuel: 20 }, minutes: 240, requiresLevel: 12, effect: 'Scouts search 6 times a loop and bring 25% more' },
      { level: 4, cost: { scrap: 550, wood: 280, metal: 240, fuel: 40 }, minutes: 360, requiresLevel: 15, effect: 'Scouts can go to the Downtown' },
      { level: 5, cost: { scrap: 800, wood: 400, metal: 360, fuel: 60 }, minutes: 600, requiresLevel: 18, effect: 'Your tracks lose 50% less, scouts bring 50% more' },
    ],
  },
};

export const WORKSHOP_MAX_RARITY = ['common', 'common', 'uncommon', 'rare', 'epic', 'epic'] as const;

export const BARRICADE_GHOST_REDUCTION = [0, 0.2, 0.35, 0.35, 0.35, 0.5];

export const BUILDING_ORDER: BuildingId[] = ['workshop', 'storage', 'medical_tent', 'barricade'];

export function buildingLevelDef(id: BuildingId, level: number): BuildingLevel | undefined {
  return BUILDINGS[id].levels.find((l) => l.level === level);
}

/** Krug 5 (K9.1): the next levels the survivor can start soon, up to this many levels above. */
export const BUILDING_NEEDS_REACH = 2;

// ---- Building site (23. 10. 2026, owner, a player's feedback) ------------------------------------------------

/**
 * 23. 10. 2026 (owner, a player's feedback: "maxed storage but too low level to build, and then throwing away what I need
 * for the next structures"): a building site. The player puts resources toward a building's next level, at most up to that
 * level's cost; what is on the site leaves Storage (it takes no room) and can't be taken back. It opens for the next level
 * within the reach the building needs already use (its level requirement at most the survivor's level +
 * `BUILDING_NEEDS_REACH`), also while that level is still locked by the survivor's level; never for a building going up
 * now or past its last level. Starting the upgrade takes the cost less the site from Storage and clears the site.
 * `on: false` turns it off (no site on the sheet, the route refuses; what is already on a site still counts).
 */
export const BUILDING_SITE = { on: true } as const;

/** What is on one building's site: the level it is for and the resources put there. */
export interface BuildingSite {
  level: number;
  stacks: StackMap;
}

/** The sites of a survivor, one a building (kept in `survivors.record.sites`, no column of its own). */
export type BuildingSites = Partial<Record<BuildingId, BuildingSite>>;

/** Why a building's next level can't take resources now: the switch, going up now, the last level, out of reach. */
export type SiteBlock = 'off' | 'upgrading' | 'max' | 'reach';

/** What is on the site for this level (a site left for another level counts as nothing). */
export function sitePaid(sites: BuildingSites | null | undefined, id: BuildingId, level: number): StackMap {
  const site = sites?.[id];
  return site && site.level === level ? { ...site.stacks } : {};
}

/** The cost less what is on the site: what the upgrade still takes from Storage. */
export function siteLeft(cost: StackMap, paid: StackMap): StackMap {
  const out: StackMap = {};
  for (const [k, v] of Object.entries(cost) as [StackKind, number][]) {
    const left = (v ?? 0) - (paid[k] ?? 0);
    if (left > 0) out[k] = left;
  }
  return out;
}

/** The survivor level a level's site opens at (its level requirement less the reach). */
export const siteFromLevel = (requiresLevel: number) => Math.max(1, requiresLevel - BUILDING_NEEDS_REACH);

/** Whether the next level of a building at `level` takes resources now, or why not. */
export function siteBlocked(id: BuildingId, level: number, survivorLevel: number, upgrading: boolean): SiteBlock | null {
  if (!BUILDING_SITE.on) return 'off';
  if (upgrading) return 'upgrading';
  const next = buildingLevelDef(id, level + 1);
  if (!next) return 'max';
  return survivorLevel >= siteFromLevel(next.requiresLevel) ? null : 'reach';
}

/**
 * 23. 10. 2026 (the lead's choice, the site's open question 1): the site takes only what takes Storage room. Scrap (the
 * currency) and the specials stay in Storage and are paid when the upgrade starts, so one tap never locks up the Scrap.
 */
export const siteTakes = (k: StackKind) => k !== 'scrap' && STACKS[k]?.category !== 'special';

/**
 * What one tap puts on the site: from Storage, as much as there is, up to what the level still needs (`left`), only what
 * takes Storage room (`siteTakes`); `want` (a partial put) caps it per resource.
 */
export function siteTake(left: StackMap, storage: StackMap, want?: StackMap | null): StackMap {
  const out: StackMap = {};
  for (const [k, v] of Object.entries(left) as [StackKind, number][]) {
    if (!siteTakes(k)) continue;
    const n = Math.floor(Math.min(v ?? 0, Math.max(0, storage[k] ?? 0), want ? Math.max(0, want[k] ?? 0) : Infinity));
    if (n > 0) out[k] = n;
  }
  return out;
}

/** The sites with `stacks` added to a building's site for `level` (a site left for another level is replaced). */
export function siteAdd(sites: BuildingSites | null | undefined, id: BuildingId, level: number, stacks: StackMap): BuildingSites {
  const paid = sitePaid(sites, id, level);
  for (const [k, v] of Object.entries(stacks) as [StackKind, number][]) if (v) paid[k] = (paid[k] ?? 0) + v;
  return { ...(sites ?? {}), [id]: { level, stacks: paid } };
}

/** The sites without this building's (its upgrade started). */
export function siteClear(sites: BuildingSites | null | undefined, id: BuildingId): BuildingSites {
  const out: BuildingSites = { ...(sites ?? {}) };
  delete out[id];
  return out;
}

/**
 * The next level of every building within reach (survivor level + 2, not the one going up now). 23. 10. 2026: with
 * `sites`, a level costs what it still takes (its building site counts as paid).
 */
function nextLevelsInReach(levels: Partial<Record<string, number>>, survivorLevel: number, busy: readonly string[], sites?: BuildingSites | null): { id: BuildingId; next: BuildingLevel }[] {
  const out: { id: BuildingId; next: BuildingLevel }[] = [];
  for (const id of BUILDING_ORDER) {
    if (busy.includes(id)) continue;
    const next = buildingLevelDef(id, (levels[id] ?? BUILDINGS[id].startLevel) + 1);
    if (next && next.requiresLevel <= survivorLevel + BUILDING_NEEDS_REACH) out.push({ id, next: sites ? { ...next, cost: siteLeft(next.cost as StackMap, sitePaid(sites, id, next.level)) } : next });
  }
  return out;
}

/** What all those next levels cost together (K11: a resource above this is surplus); 23. 10. 2026: less their building sites. */
export function buildingCostsInReach(levels: Partial<Record<string, number>>, survivorLevel: number, busy: readonly string[] = [], sites?: BuildingSites | null): StackMap {
  const total: StackMap = {};
  for (const { next } of nextLevelsInReach(levels, survivorLevel, busy, sites)) for (const [k, v] of Object.entries(next.cost) as [StackKind, number][]) total[k] = (total[k] ?? 0) + v;
  return total;
}

/**
 * K11: while the upgrades within reach need more of `k` together than Storage has (as K9.1 counts
 * it), the first of them that asks for it ("Needed for Storage LV 3"); else null.
 */
export function buildingNeedingFor(levels: Partial<Record<string, number>>, survivorLevel: number, have: StackMap, k: StackKind, busy: readonly string[] = [], sites?: BuildingSites | null): { id: BuildingId; name: string; level: number } | null {
  const reach = nextLevelsInReach(levels, survivorLevel, busy, sites);
  const total = reach.reduce((a, { next }) => a + ((next.cost as StackMap)[k] ?? 0), 0);
  if (total <= (have[k] ?? 0)) return null;
  const hit = reach.find(({ next }) => ((next.cost as StackMap)[k] ?? 0) > 0);
  return hit ? { id: hit.id, name: BUILDINGS[hit.id].name, level: hit.next.level } : null;
}

/**
 * Krug 5 (K9.1): what the next building upgrades within reach still need, per resource: the next
 * level of every building whose level requirement is at most the survivor's level + 2 (a building
 * going up now is left out), all together, minus what Storage already has. 23. 10. 2026 (owner): what is on a building
 * site counts as paid (`sites`), so a full Storage no longer keeps what is already there.
 */
export function buildingNeeds(levels: Partial<Record<string, number>>, survivorLevel: number, have: StackMap, busy: readonly string[] = [], sites?: BuildingSites | null): StackMap {
  const total = buildingCostsInReach(levels, survivorLevel, busy, sites);
  const out: StackMap = {};
  for (const [k, v] of Object.entries(total) as [StackKind, number][]) {
    const miss = v - (have[k] ?? 0);
    if (miss > 0) out[k] = miss;
  }
  return out;
}
